import {
  IconAlertHexagon,
  IconBuildingBank,
  IconClipboardList,
  IconCoin,
  IconPaperclip,
  IconUser,
} from '@u21/tabler-icons';
import { FormConfig, FormField } from 'app/modules/filingFormGenerator/models';
import { GoamlReferenceTables } from 'app/modules/goAML/queries/useGetGoamlFiuConfig';
import { GoamlGeneratedFormValues } from 'app/modules/goAML/formConfig/models';
import { GoamlAttachmentsTab } from 'app/modules/goAML/components/GoamlAttachmentsTab';
import { getFilingInfoFields } from 'app/modules/goAML/formConfig/fields/filingInfoFields';
import { getLocationFields } from 'app/modules/goAML/formConfig/fields/locationFields';
import { getReportingPersonFields } from 'app/modules/goAML/formConfig/fields/reportingPersonFields';
import { getTransactionFields } from 'app/modules/goAML/formConfig/fields/transactionFields';
import { selectField } from 'app/modules/goAML/formConfig/fields/fieldBuilders';

export const GOAML_GENERATED_TABS = {
  FILING_INFO: 'filing-info',
  REPORTING_PERSON: 'reporting-person',
  LOCATION: 'location',
  TRANSACTIONS: 'transactions',
  INDICATORS: 'indicators',
  ATTACHMENTS: 'attachments',
} as const;

const SECTION_FLAGS = {
  mustExistInPayload: true,
  mustHaveValueIfExist: false,
} as const;

export const getGoamlGeneratedFormConfig = (
  referenceTables: GoamlReferenceTables,
): FormConfig<GoamlGeneratedFormValues>[] => [
  {
    tabLabel: 'Filing Info',
    tabKey: 'filing_info',
    tabIcon: <IconClipboardList />,
    tabPath: GOAML_GENERATED_TABS.FILING_INFO,
    tabSection: {
      type: 'tabSection',
      name: 'filing_info',
      label: 'Filing Info',
      fields: getFilingInfoFields(referenceTables),
      ...SECTION_FLAGS,
    },
  },
  {
    tabLabel: 'Reporting Person',
    tabKey: 'reporting_person',
    tabIcon: <IconUser />,
    tabPath: GOAML_GENERATED_TABS.REPORTING_PERSON,
    tabSection: {
      type: 'tabSection',
      name: 'reporting_person',
      label: 'Reporting Person',
      fields: getReportingPersonFields(referenceTables),
      ...SECTION_FLAGS,
    },
  },
  {
    tabLabel: 'Reporting Entity',
    tabKey: 'location',
    tabIcon: <IconBuildingBank />,
    tabPath: GOAML_GENERATED_TABS.LOCATION,
    tabSection: {
      type: 'tabSection',
      name: 'location',
      label: 'Reporting Entity Location',
      fields: getLocationFields(referenceTables),
      ...SECTION_FLAGS,
    },
  },
  {
    tabLabel: 'Transactions',
    tabKey: 'transaction',
    tabIcon: <IconCoin />,
    tabPath: GOAML_GENERATED_TABS.TRANSACTIONS,
    tabSection: {
      type: 'tabSection',
      name: 'transaction',
      label: 'Transaction',
      repeatable: true,
      repeatableButtonLabel: 'Transaction',
      defaultNumOfEntries: 0,
      fields: getTransactionFields(referenceTables),
      ...SECTION_FLAGS,
    },
  },
  {
    tabLabel: 'Indicators',
    tabKey: 'report_indicators',
    tabIcon: <IconAlertHexagon />,
    tabPath: GOAML_GENERATED_TABS.INDICATORS,
    tabSection: {
      type: 'tabSection',
      name: 'report_indicators',
      label: 'Report Indicator',
      repeatable: true,
      repeatableButtonLabel: 'Indicator',
      defaultNumOfEntries: 1,
      fields: [
        {
          ...selectField(
            {
              name: 'indicator',
              label: 'Indicator',
              required: true,
              refTable: 'report_indicators',
            },
            referenceTables,
          ),
          payloadErrorPathToFormErrorPathConversion: (
            indexes?: number[],
            isWildcard?: boolean,
          ) => ({
            payloadPath: ['report_indicators'],
            formPath: isWildcard
              ? 'report_indicators[*].indicator'
              : `report_indicators[${indexes?.[0] ?? 0}].indicator`,
          }),
        } satisfies FormField<Record<string, any>>,
      ],
      ...SECTION_FLAGS,
    },
  },
  {
    tabLabel: 'Attachments',
    tabKey: 'associated_files',
    tabIcon: <IconPaperclip />,
    tabPath: GOAML_GENERATED_TABS.ATTACHMENTS,
    tabSection: {
      type: 'tabSection',
      name: 'associated_files',
      label: 'Attachments',
      fields: [],
      mustExistInPayload: false,
      mustHaveValueIfExist: false,
    },
    tabContent: <GoamlAttachmentsTab />,
  },
];
